import { cn } from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { CustomFormField } from "@/components";
import { Input } from "@/components/ui/input";

import { stylistRegistrationFormFields } from "@/utils/FormFields";
import { FormFieldConfig } from "@/types/AppTypes";

import { LoaderCircle, X } from "lucide-react";

import { useSignUpUser } from "@/hooks";
import { stylistRegistrationSchema } from "@/schemas/stylistRegistrationSchema";
import { uploadFileToBucket } from "@/utils/helper/aws-bucketUpload";
import { toast } from "sonner";

export default function StylistRegistrationForm({
  className,
  ...props
}: React.ComponentPropsWithoutRef<"div">) {
  const { handleSignUp } = useSignUpUser();
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [loading, setLoading] = useState(false);

  const form = useForm<z.infer<typeof stylistRegistrationSchema>>({
    resolver: zodResolver(stylistRegistrationSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      confirmPassword: "",
      salonName: "",
      license: undefined,
    },
  });

  const handleImageChange = (files: FileList | null, onChange: (file: File) => void) => {
    if (files && files[0]) {
      const selectedFile = files[0];
      onChange(selectedFile);

      // Create preview for images only
      const previewUrl = URL.createObjectURL(selectedFile);
      setImagePreview(previewUrl);
    }
  };

  const handleRemoveImage = () => {
    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
      setImagePreview(null);
      form.setValue("license", null as any);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const renderFormField = (field: FormFieldConfig) => (
    <CustomFormField
      key={field.name || ""}
      control={form.control}
      name={field.name || "unnamed"}
      label={field.label || ""}
      placeholder={field.placeholder}
      type={field.type || "text"}
      className={field.className}
      required={field.required || false}
      disabled={field.disabled || false}
    />
  );

  async function onSubmit(values: z.infer<typeof stylistRegistrationSchema>) {
    try {
      setLoading(true);
      let license_path;
      if (values.license instanceof File) {
        license_path = await uploadFileToBucket(values.license, "stylist-license-pictures");
      } else if (!values.license) {
        license_path = "";
        toast.error("License is required");
        return;
      }
  
      const formData = { ...values, license: license_path };
  
      await handleSignUp({ ...formData, autoNavigate: true, });
      
    } catch (error) {
          console.log("error:::::  ", error);      
    }finally{
      setLoading(false);
    }
   
  }

  return (
    <div
      className={cn("flex flex-col gap-6 shadow-sm w-full h-full rounded-lg ", className)}
      {...props}
    >
      <Card>
        <CardHeader className="">
          <CardTitle className="text-2xl">Register</CardTitle>
          <CardDescription className="text-palette-text">
            Create a new beauty professional account
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <div className="flex flex-col md:flex-row gap-4">
                {stylistRegistrationFormFields.slice(0, 2).map((field) => (
                  <div key={field.name} className="flex-1">
                    {renderFormField(field)}
                  </div>
                ))}
              </div>

              {stylistRegistrationFormFields.slice(2, -1).map(renderFormField)}

              {/* License Upload Field */}
              <FormField
                control={form.control}
                name="license"
                render={({ field: { onChange, value, ref, ...rest } }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium text-gray-900">
                      Professional License
                    </FormLabel>
                    <FormControl>
                      <div className="flex flex-col gap-2">
                        <div className="flex items-center gap-2 mb-2">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={loading}
                          >
                            Choose File
                          </Button>
                          <span className="text-sm text-gray-500">
                            {form.watch("license")
                              ? `File selected: ${(form.watch("license") as File)?.name}`
                              : "No file selected"}
                          </span>
                        </div>
                        <div className="text-xs text-gray-400 mb-2">
                          Allowed file types: jpeg, jpg, png
                        </div>
                        <Input
                          type="file"
                          accept="image/jpeg,image/jpg,image/png"
                          ref={fileInputRef}
                          onChange={(e) => handleImageChange(e.target.files, onChange)}
                          disabled={loading}
                          className="hidden"
                        />
                        {form.watch("license") && imagePreview ? (
                          <div className="mt-2 relative">
                            <div className="border border-gray-200 rounded-md p-2 w-fit relative">
                              <img
                                src={imagePreview}
                                alt="License preview"
                                className="max-w-[200px] max-h-[200px] object-contain"
                              />
                              <X
                                onClick={handleRemoveImage}
                                className="absolute -top-2 -right-2 z-10 w-5 h-5 cursor-pointer bg-white rounded-full p-1 text-black border border-gray-300"
                              />
                            </div>
                          </div>
                        ) : null}
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button
                disabled={loading}
                type="submit"
                className="w-full bg-black text-white hover:bg-gray-800 h-11"
              >
                {loading ? (
                  <>
                    <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
                    <span>Please wait...</span>
                  </>
                ) : (
                  "Create Account"
                )}
              </Button>

              <div className="text-center text-sm text-gray-500 mt-4"></div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
